import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    createFinancialStrengthRatio,
    deleteFinancialStrengthRatio,
} from '@/modules/Report/components/BusinessPerformance/services'
import {
    FinancialStrengthRatio,
    FinancialStrengthRatioCreateParams,
} from '@/modules/Report/components/BusinessPerformance/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import * as yup from 'yup'

export interface RatioFormModalProps extends ModalProps {
    refetch?: () => void
    editData?: FinancialStrengthRatio | null
    mode: 'create' | 'edit'
}

interface FormData {
    report_year: number
    report_month: number
    current_ratio: number
    quick_ratio: number
    interest_coverage: number
    debt_to_equity_ratio: number
    notes?: string
}

const currentYear = dayjs().year()

const RatioFormModal: React.FC<RatioFormModalProps> = ({
    open,
    onClose,
    refetch,
    editData,
    mode,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                report_month: yup
                    .string()
                    .required(t('required'))
                    .matches(/^\d{4}-\d{2}$/, t('invalid_month_format')),
                current_ratio: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                quick_ratio: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                interest_coverage: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                debt_to_equity_ratio: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                notes: yup.string(),
            }),
        [t]
    )

    // Default values
    const defaultValues: FormData = useMemo(
        () => ({
            report_year: dayjs().year(),
            report_month: dayjs().month() + 1,
            current_ratio: 0,
            quick_ratio: 0,
            interest_coverage: 0,
            debt_to_equity_ratio: 0,
            notes: '',
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    // Reset form when dialog opens/closes or editData changes
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && editData) {
                const [year, month] = editData.report_month.split('-')
                reset({
                    report_year: parseInt(year || '0'),
                    report_month: parseInt(month || '0'),
                    current_ratio: editData.current_ratio,
                    quick_ratio: editData.quick_ratio,
                    interest_coverage: editData.interest_coverage,
                    debt_to_equity_ratio: editData.debt_to_equity_ratio,
                    notes: editData.notes || '',
                })
            } else {
                reset(defaultValues)
            }
        }
    }, [open, editData, mode, reset, defaultValues])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)

            const payload: FinancialStrengthRatioCreateParams = {
                report_month: `${values.report_year}-${values.report_month}`,
                current_ratio: values.current_ratio,
                quick_ratio: values.quick_ratio,
                interest_coverage: values.interest_coverage,
                debt_to_equity_ratio: values.debt_to_equity_ratio,
                notes: values.notes || '',
            }

            // Upsert (create or update based on report_month)
            await createFinancialStrengthRatio(payload)

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_FINANCIAL_STRENGTH_RATIOS],
            })

            // Show success message
            toast.success(
                mode === 'edit' ? t('update-success') : t('create-success')
            )

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to save financial strength ratio:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!editData) return

        const confirmed = window.confirm(
            t('confirm_delete_financial_strength_ratio')
        )
        if (!confirmed) return

        try {
            setIsDeleting(true)

            await deleteFinancialStrengthRatio({
                report_month: editData.report_month,
            })

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_FINANCIAL_STRENGTH_RATIOS],
            })

            // Show success message
            toast.success(t('delete-success'))

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to delete financial strength ratio:', error)
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                mode === 'edit'
                    ? t('edit_financial_strength_ratio')
                    : t('create_financial_strength_ratio')
            }
            maxWidth="md"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                <FormItem
                    control={control}
                    name="report_year"
                    type="select"
                    label={t('report_year')}
                    options={Array.from({ length: 12 }, (_, index) => ({
                        label: `${t('year')} ${currentYear - index}`,
                        value: currentYear - index,
                    }))}
                    allowClear={false}
                    disabled={mode === 'edit'}
                />
                {/* Report Month */}
                <FormItem
                    control={control}
                    name="report_month"
                    type="select"
                    label={t('report_month')}
                    options={Array.from({ length: 12 }, (_, index) => ({
                        label: `${t('month')} ${index + 1}`,
                        value: index + 1,
                    }))}
                    allowClear={false}
                    disabled={mode === 'edit'}
                />

                {/* Current Ratio */}
                <FormItem
                    control={control}
                    name="current_ratio"
                    type="number"
                    label={t('current_ratio')}
                />

                {/* Quick Ratio */}
                <FormItem
                    control={control}
                    name="quick_ratio"
                    type="number"
                    label={t('quick_ratio')}
                    placeholder={t('enter_quick_ratio')}
                />

                {/* Interest Coverage */}
                <FormItem
                    control={control}
                    name="interest_coverage"
                    type="number"
                    label={t('interest_coverage')}
                    placeholder={t('enter_interest_coverage')}
                />

                {/* Debt to Equity Ratio */}
                <FormItem
                    control={control}
                    name="debt_to_equity_ratio"
                    type="number"
                    label={t('debt_to_equity_ratio')}
                    placeholder={t('enter_debt_to_equity_ratio')}
                />

                {/* Notes */}
                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                    placeholder={t('enter_notes')}
                />

                {/* Actions */}
                <div className="flex justify-between">
                    <div>
                        {mode === 'edit' && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={isSubmitting || isDeleting}
                                loading={isDeleting}
                            >
                                {t('delete')}
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting || isDeleting}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting || isDeleting}
                            loading={isSubmitting}
                        >
                            {mode === 'edit' ? t('update') : t('create')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default RatioFormModal
